<script setup>
import RegisteredDevices from '@/components/RegisteredDevices.vue'
import RewardCard from '@/components/RewardCard.vue'
import {useGlobalStore} from '@/store'
import CustomCheckbox from '@/components/Form/CustomCheckbox.vue'
import {computed} from 'vue'
import {useSmoothScroll} from '@/composables/useSmoothScroll'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const globalStore = useGlobalStore()
const stepTwo = computed(() => {
  return globalStore.stepTwo ? globalStore.stepTwo : null
})
const stepFour = computed(() => {
  return globalStore.stepFour ? globalStore.stepFour : null
})
const selectedReward = globalStore.findSelectedReward()
const detailUrl = globalStore.detailPageUrl
const participationsConditionsLinkText = t('stepFour.participationConditionsLinkText')
const participationsConditionsDynamicLink = `<a href="${globalStore.termsOfParticipationPageUrl}" target="_blank">${participationsConditionsLinkText}</a>`
const participationsConditionsDynamicLabel = t('stepFour.participationConditionsCheckboxLabel', { link: participationsConditionsDynamicLink })
const dataPrivacyLinkText = t('stepFour.dataPrivacyLinkText')
const dataPrivacyDynamicLink = `<a href="${globalStore.privacyPageUrl}" target="_blank">${dataPrivacyLinkText}</a>`
const dataPrivacyDynamicLabel = t('stepFour.dataPrivacyCheckboxLabel', { link: dataPrivacyDynamicLink })
function updatedNewsletterCheckbox (newValue)
{
  stepFour.value.isNewsletterChecked = newValue
}

function updatedConditionsOfParticipationCheckbox (newValue)
{
  stepFour.value.isConditionsOfParticipationChecked = newValue
}

function updatedDataPrivacyCheckbox (newValue)
{
  stepFour.value.isDataPrivacyChecked = newValue
}

const { scrollToId } = useSmoothScroll()

function handleStepChange (step)
{
  globalStore.goToStep(step)

  if (!globalStore.isErrorState) {
    scrollToId('reward-campaign', 175)
  }
}
</script>

<template>
  <div class="form-view--step form-view--step-four">
    <div class="form-view--step-header">
      <h2>{{ $t('stepFour.title') }}</h2>
      <span>{{ $t('stepFour.description') }}</span>
    </div>
    <div class="form-view--step-content">

      <div class="form-view--step-content-registered">
        <span class="bold delta">{{ $t('stepFour.registeredDevices.label') }}</span>
        <RegisteredDevices />
      </div>

      <div class="form-view--step-content-customer-data">
        <div class="form-view--step-content-customer-personal-data">
          <span class="bold delta">{{ $t('stepFour.customerData.labelPersonal') }}</span>
          <div>
            <span v-if="stepTwo?.personalData.firstname"><span class="bold">{{ $t('stepFour.customerData.firstname') }}</span> {{stepTwo.personalData.firstname}}</span>
            <span v-if="stepTwo?.personalData.lastname"><span class="bold">{{ $t('stepFour.customerData.lastname') }}</span> {{stepTwo.personalData.lastname}}</span>
            <span v-if="stepTwo?.personalData.email"><span class="bold">{{ $t('stepFour.customerData.email') }}</span> {{stepTwo.personalData.email}}</span>
            <span v-if="stepTwo?.personalData.phone"><span class="bold">{{ $t('stepFour.customerData.phone') }}</span> {{stepTwo.personalData.phone}}</span>
            <span v-else><span class="bold">{{ $t('stepFour.customerData.phone') }}</span> -</span>
          </div>
        </div>
        <div class="form-view--step-content-customer-delivery-data">
          <span class="bold delta">{{ $t('stepFour.customerData.labelDelivery') }}</span>
          <div>
            <span v-if="stepTwo?.deliveryData.company"><span class="bold">{{ $t('stepFour.customerData.company') }}</span> {{stepTwo.deliveryData.company}}</span>
            <span v-if="stepTwo?.deliveryData.firstname"><span class="bold">{{ $t('stepFour.customerData.firstname') }}</span> {{stepTwo.deliveryData.firstname}}</span>
            <span v-if="stepTwo?.deliveryData.lastname"><span class="bold">{{ $t('stepFour.customerData.lastname') }}</span> {{stepTwo.deliveryData.lastname}}</span>
            <span v-if="stepTwo?.deliveryData.street"><span class="bold">{{ $t('stepFour.customerData.street') }}</span> {{stepTwo.deliveryData.street}}</span>
            <span v-if="stepTwo?.deliveryData.houseNo"><span class="bold">{{ $t('stepFour.customerData.houseNo') }}</span> {{stepTwo.deliveryData.houseNo}}</span>
            <span v-if="stepTwo?.deliveryData.zip"><span class="bold">{{ $t('stepFour.customerData.zip') }}</span> {{stepTwo.deliveryData.zip}}</span>
            <span v-if="stepTwo?.deliveryData.city"><span class="bold">{{ $t('stepFour.customerData.city') }}</span> {{stepTwo.deliveryData.city}}</span>
          </div>
        </div>
        <div>
          <span @click="handleStepChange(2)" class="-color-alpha icon-caret-right-before zeta bold form-view--step-change">{{ $t('stepFour.customerData.editLabel') }}</span>
        </div>
      </div>

      <div class="form-view--step-content-chosen-reward">
        <span class="bold delta">{{ $t('stepFour.rewardData.label') }}</span>
        <RewardCard v-if="selectedReward.isManualReward" :reward="selectedReward" :is-manual-reward="true" />
        <RewardCard v-else :reward="selectedReward" :detail-page-url="detailUrl" />
        <span @click="handleStepChange(3)" class="-color-alpha icon-caret-right-before zeta bold form-view--step-change">{{ $t('stepFour.rewardData.editLabel') }}</span>
      </div>

      <div class="form-view--step-content-checkboxes">
        <CustomCheckbox @updated="updatedNewsletterCheckbox($event)" :checked="stepFour.isNewsletterChecked" :label="$t('stepFour.newsletterCheckboxLabel')" :required="false" :id="'reward-campaign-step-4-isNewsletterChecked'" />
        <CustomCheckbox @updated="updatedConditionsOfParticipationCheckbox($event)" :checked="stepFour.isConditionsOfParticipationChecked" :label="participationsConditionsDynamicLabel" :required="true" :id="'reward-campaign-step-4-isConditionsOfParticipationChecked'" />
        <CustomCheckbox @updated="updatedDataPrivacyCheckbox($event)" :checked="stepFour.isDataPrivacyChecked" :label="dataPrivacyDynamicLabel" :required="true" :id="'reward-campaign-step-4-isDataPrivacyChecked'" />
      </div>
    </div>
  </div>
</template>
